import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';

/**
 * The pages a person has open, as tabs along the top bar (owner, 6 October 2026: "if you open say
 * extensions its a tab on the top bar, then if you open dialing restrictions it's a new tab then
 * we can switch between them easily, also close them with a x").
 *
 * A tab is a section, and it remembers where in the section it was: the seat, and which of its
 * settings, under Extensions. Going back to a tab goes back there, at the top of the page.
 * The address is still the truth -- a tab is opened by being navigated to, from the menu, a link
 * or a typed address -- and the list is kept for this browser tab only (sessionStorage), so a
 * second window starts with the page it opened.
 *
 * What a page holds that is not in its address -- something typed and not yet saved -- is not
 * kept: switching away unmounts the page, as following any link does. Most settings save as they
 * change.
 */
export interface PageTab {
  /** The section: one tab each. */
  key: string;
  /** Where in it the tab was last. */
  path: string;
  /** When it was last shown, for choosing which to close when there are too many. */
  used: number;
}

/** More than this and the least recently shown one closes: past it, labels shrink to nothing. */
const MAX_TABS = 10;

function load(storageKey: string): PageTab[] {
  try {
    const v: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? '[]');
    if (!Array.isArray(v)) return [];
    return v.filter((t): t is PageTab => !!t && typeof t === 'object'
      && typeof (t as PageTab).key === 'string' && typeof (t as PageTab).path === 'string'
      && (t as PageTab).path.startsWith('/'))
      .map((t) => ({ key: t.key, path: t.path, used: typeof t.used === 'number' ? t.used : 0 }));
  } catch {
    return [];
  }
}

function save(storageKey: string, tabs: PageTab[]): void {
  try { sessionStorage.setItem(storageKey, JSON.stringify(tabs)); } catch { /* private window: tabs last the visit */ }
}


export interface PageTabs {
  tabs: PageTab[];
  /** The tab showing, when the page showing is one. */
  current: string | null;
  /** Back to a tab, where it was left. */
  show: (key: string) => void;
  /** Closes a tab; closing the one showing shows its neighbour. */
  close: (key: string) => void;
}

export function usePageTabs({ storageKey, current, path, home, fits, startOf }: {
  /** Whose tabs these are: the console's, or one account's. */
  storageKey: string;
  /** The section showing, or null for a page that is not one. */
  current: string | null;
  /** The address showing. */
  path: string;
  /** Where to go when the tab showing closes and there is no other. */
  home: string;
  /**
   * Whether an address belongs to a tab, and where a tab starts. The account's console works out its
   * section a moment after its address changes, and a tab saved in between kept another section's
   * address: its Dashboard tab opened Extensions (owner, 21 October 2026). With these, an address is
   * saved only to its own tab, and a tab holding a wrong one opens at its start.
   */
  fits?: (key: string, path: string) => boolean;
  startOf?: (key: string) => string;
}): PageTabs {
  const router = useRouter();
  const qc = useQueryClient();
  const [store, setStore] = useState(() => ({ key: storageKey, tabs: load(storageKey) }));
  // Another account's console: its own tabs.
  const active = store.key === storageKey ? store : { key: storageKey, tabs: load(storageKey) };
  if (active !== store) setStore(active);

  /**
   * The tab just closed while its page was showing. Until the address has moved on, the page still
   * on screen must not open it again: that was a closed tab coming back, and its address staying
   * (owner, 21 October 2026: "when you close one the url seems to keep it open").
   */
  const closing = useRef<string | null>(null);

  // Whatever is showing is a tab, at the address it is showing.
  useEffect(() => {
    if (!current) return;
    if (closing.current === current) return;
    if (fits && !fits(current, path)) return;
    closing.current = null;
    setStore((s) => {
      const now = Date.now();
      let tabs = s.tabs.some((t) => t.key === current)
        ? s.tabs.map((t) => (t.key === current ? { ...t, path, used: now } : t))
        : [...s.tabs, { key: current, path, used: now }];
      while (tabs.length > MAX_TABS) {
        const oldest = tabs.filter((t) => t.key !== current).sort((a, b) => a.used - b.used)[0];
        tabs = tabs.filter((t) => t !== oldest);
      }
      save(s.key, tabs);
      return { ...s, tabs };
    });
  }, [current, path, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * A tab opens at the top (owner, 21 October 2026: "clicking on a tab should reload it at the top"),
   * where it was left in its section -- the seat and its settings -- but not scrolled down the page.
   */
  const go = useCallback((to: string) => {
    router.history.push(to);
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [router]);

  const show = useCallback((key: string) => {
    const tab = active.tabs.find((t) => t.key === key);
    if (!tab) return;
    if (key === current) {
      // The tab showing, clicked again: read afresh and back to the top, as a browser's reload is.
      window.scrollTo(0, 0);
      void qc.invalidateQueries({ type: 'active' });
      return;
    }
    go(fits && startOf && !fits(key, tab.path) ? startOf(key) : tab.path);
  }, [active.tabs, current, go, qc, fits, startOf]);

  const close = useCallback((key: string) => {
    const i = active.tabs.findIndex((t) => t.key === key);
    if (i < 0) return;
    const tab = active.tabs[i]!;
    const rest = active.tabs.filter((t) => t.key !== key);
    // Its page is the one showing by its section, or by the address itself.
    const showing = key === current || path === tab.path || path.startsWith(`${tab.path}/`);
    save(storageKey, rest);
    setStore({ key: storageKey, tabs: rest });
    if (!showing) return;
    closing.current = key;
    const next = rest[i] ?? rest[i - 1];
    go(!next ? home : fits && startOf && !fits(next.key, next.path) ? startOf(next.key) : next.path);
  }, [active.tabs, current, go, home, path, storageKey, fits, startOf]);

  return { tabs: active.tabs, current, show, close };
}
